import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../store/signupSlice";
import "./Dashboard.css";
import { io } from "socket.io-client";
import ChatSidebar from "./ChatSidebar";
import TopBar from "../components/TopBar";
import logo from "./logo.png";
import { apiFetch } from "../utils/apiFetch";

export default function Dashboard() {
  const [requests, setRequests] = useState([]);
  const [requested, setRequested] = useState([]);
  const [friends, setFriends] = useState([]);
  const [needFollowBack, setNeedFollowBack] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [lastMessages, setLastMessages] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const signupUser = useSelector(state => state.signup.user);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const notifRef = useRef();
  const msgRef = useRef();
  const [currentUser, setCurrentUser] = useState(null);
  const dispatch = useDispatch();
  const socketRef = useRef(null); // <--- useRef for socket to persist across renders

  // Fetch initial dashboard and user data
  useEffect(() => {
    setLoading(true);

    let mounted = true;

    // Fetch dashboard data (requests, requested, friends)
    apiFetch("https://razchat-backend-1.onrender.com/api/users/dashboard")
      .then(res => res && res.json())
      .then(data => {
        if (!mounted) return;
        if (data) {
          setRequests(data.requests || []);
          setRequested(data.requested || []);
          setFriends(data.friends || []);
        }
        setLoading(false);
      })
      .catch(() => {
        setMsg("Network error: Could not fetch users");
        setSnackbarOpen(true);
        setLoading(false);
      });

    // Fetch all users for suggested users
    apiFetch("https://razchat-backend-1.onrender.com/api/users/all")
      .then(res => res && res.json())
      .then(users => {
        if (!mounted) return;
        if (users) setAllUsers(users || []);
      })
      .catch(() => {});

    // If redux user is missing, try to restore from token/email
    if (!signupUser) {
      try {
        const tokenData = JSON.parse(atob(token.split('.')[1]));
        const userId = tokenData.userId;
        if (userId) {
          apiFetch(`https://razchat-backend-1.onrender.com/api/users/${userId}`)
            .then(res => res && res.json())
            .then(user => {
              if (user && user.username && user.email) {
                dispatch(setUser({ username: user.username, email: user.email, _id: user._id }));
                setCurrentUser({ username: user.username, email: user.email, _id: user._id });
              }
            });
        }
      } catch {
        // fallback: do nothing
      }
    } else {
      setCurrentUser(signupUser);
    }

    // Setup socket.io for real-time notifications and messages
    const s = io("https://razchat-backend-1.onrender.com", { transports: ["websocket"] });
    socketRef.current = s;
    // Join your own user room for notifications
    let myUserId = null;
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      myUserId = tokenData.userId;
      if (myUserId) s.emit("joinRoom", myUserId);
    } catch {}
    
    // Listen for notification events
    s.on("notification", (notif) => {
      if (notif.type === "follow-request") {
        setRequests(prev => [...prev, notif.user]);
        setSnackbarOpen(true);
        setMsg(`${notif.user.username} sent you a follow request!`);
      }
      if (notif.type === "follow-accepted") {
        setFriends(prev => [...prev, notif.user]);
        setSnackbarOpen(true);
        setMsg(`${notif.user.username} accepted your follow request!`);
      }
    });
    
    // Listen for new message events
    s.on("newMessage", (msg) => {
      // Only show notification if you are the receiver and this is not your own message
      let myId = currentUser?._id || signupUser?._id;
      if (msg.receiver === myId && !msg.isMine) {
        setMsg("New message from " + (msg.senderName || "someone"));
        setSnackbarOpen(true);
      }
      // Always update the sidebar/chat list
      setLastMessages(prev => {
        let otherId = msg.sender === myId ? msg.receiver : msg.sender;
        if (!otherId) {
          otherId = msg.sender;
        }
        return {
          ...prev,
          [otherId]: {
            text: msg.content,
            createdAt: msg.createdAt,
            isMine: msg.isMine
          }
        };
      });
    });

    // ---- Listen for real-time messages and update lastMessages instantly ----
    // s.on("newMessage", (msg) => {
    //   setLastMessages(prev => {
    //     // Determine the "other user" in the chat
    //     let myId = currentUser?._id;
    //     let otherId = msg.sender === myId ? msg.receiver : msg.sender;
    //     // If for some reason we can't detect currentUser, fallback to both possibilities
    //     if (!myId) {
    //       if (signupUser?._id) myId = signupUser._id;
    //     }
    //     if (!otherId) {
    //       // fallback: just use sender
    //       otherId = msg.sender;
    //     }
    //     return {
    //       ...prev,
    //       [otherId]: {
    //         text: msg.content,
    //         createdAt: msg.createdAt,
    //         isMine: msg.sender === myId
    //       }
    //     };
    //   });
    // });
    // ---- END real-time messages listener ----

    return () => { mounted = false; s.disconnect(); };
  }, [token, signupUser, dispatch, currentUser]);

  // Fetch last message for each friend (on load or friends change)
  useEffect(() => {
    async function fetchLastMessages() {
      if (!friends || friends.length === 0) return;
      const results = {};
      await Promise.all(
        friends.map(async (user) => {
          try {
            const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/chats/last/${user._id}`);
            if (res && res.ok) {
              const data = await res.json();
              // Normalize the fields so they match the real-time message structure
              results[user._id] = {
                text: data?.content || data?.text || "",
                createdAt: data?.createdAt || data?.time || "",
                isMine: data?.sender === (currentUser?._id || signupUser?._id)
              };
            }
          } catch {
            // ignore
          }
        })
      );
      setLastMessages(results);
    }
    fetchLastMessages();
    // eslint-disable-next-line
  }, [friends, currentUser?._id, signupUser?._id]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowRequests(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Helper to get all unique users
  const getAllUniqueUsers = () => {
    const ids = new Set();
    const users = [];
    allUsers.forEach(u => {
      if (!ids.has(u._id)) {
        users.push(u);
        ids.add(u._id);
      }
    });
    return users;
  };

  const userCard = (user, actions, highlight, isSearchResult = false) => (
    <div key={user._id} className={`insta-card${highlight ? " insta-card-highlight" : ""}${isSearchResult ? " insta-search-card" : ""}`}>
      <div className="insta-avatar">
        {user.username ? user.username[0].toUpperCase() : "U"}
      </div>
      <div className="insta-card-content">
        <div className="insta-username">{user.username}</div>
        <div className="insta-email">{user.email}</div>
      </div>
      <div className="insta-card-actions">{actions}</div>
    </div>
  );

  useEffect(() => {
    // Ask for notification permission and subscribe to push
    if (window.Notification && Notification.permission !== "granted") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          subscribeUserToPush(token);
        }
      });
    } else if (window.Notification && Notification.permission === "granted") {
      subscribeUserToPush(token);
    }
    // eslint-disable-next-line
  }, [token, signupUser, dispatch]);

  // Accept handler now adds to needFollowBack state if needed
  const handleAcceptRequest = async (userId) => {
    const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/users/accept-follow/${userId}`, {
      method: "POST"
    });
    if (res && res.ok) {
      setMsg("Follow request accepted!");
      setSnackbarOpen(true);
      setRequests(requests.filter(u => u._id !== userId));
      const user = requests.find(u => u._id === userId);
      if (user) {
        setFriends([...friends, user]);
        // If we're not already following or have requested them, offer follow back
        const alreadyRequested = requested.some(r => r._id === userId);
        if (!alreadyRequested) {
          setNeedFollowBack(prev => [...prev, user]);
        }
      }
    }
  };

  // When you follow back, remove from needFollowBack and add to requested
  const handleFollowBack = async (user) => {
    const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/users/follow/${user._id}`, {
      method: "POST"
    });
    if (res && res.ok) {
      setMsg("Followed back!");
      setSnackbarOpen(true);
      setRequested([...requested, user]);
      setNeedFollowBack(needFollowBack.filter(u => u._id !== user._id));
    }
  };

  const handleRejectRequest = async (userId) => {
    const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/users/reject-follow/${userId}`, {
      method: "POST"
    });
    if (res && res.ok) {
      setMsg("Follow request rejected!");
      setSnackbarOpen(true);
      setRequests(requests.filter(u => u._id !== userId));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleSearch = async (query) => {
    const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/users/search?query=${query}`);
    if (res) {
      const data = await res.json();
      if (res.ok) {
        setSearchResults(data || []);
      } else {
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }
  };

  const clearSearchResults = () => {
    setSearchResults([]);
  }

  // Wait for currentUser before rendering most of the UI
  if (!currentUser) {
    return <div className="insta-loader" />;
  }

  return (
    <div className="insta-dashboard-bg">
      <TopBar
        logo={logo}
        notifRef={notifRef}
        msgRef={msgRef}
        requests={requests}
        requested={requested}
        friends={friends}
        showRequests={showRequests}
        setShowRequests={setShowRequests}
        handleLogout={handleLogout}
        setShowSidebar={setShowSidebar}
        onSearch={handleSearch}
      >
        {showRequests && (
          <div className="insta-dropdown">
            <div className="insta-dropdown-title">Notifications</div>
            {requests.length === 0 && requested.length === 0 && needFollowBack.length === 0 ? (
              <div className="insta-empty">No notifications.</div>
            ) : (
              <>
                {/* Follow Requests */}
                {requests.length > 0 && (
                  <>
                    <div className="insta-dropdown-subtitle">Follow Requests</div>
                    {requests.map(user =>
                      <div key={user._id} className="insta-dropdown-card">
                        <span>{user.username}</span>
                        <div>
                          <button
                            className="insta-btn insta-btn-primary"
                            onClick={() => handleAcceptRequest(user._id)}
                          >Accept</button>
                          <button
                            className="insta-btn insta-btn-danger"
                            onClick={() => handleRejectRequest(user._id)}
                          >Reject</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {/* After Accepting, show Follow Back */}
                {needFollowBack.length > 0 && (
                  <>
                    <div className="insta-dropdown-subtitle">Follow Back</div>
                    {needFollowBack.map(user =>
                      <div key={user._id} className="insta-dropdown-card">
                        <span>{user.username}</span>
                        <button
                          className="insta-btn insta-btn-primary"
                          style={{ marginLeft: 8 }}
                          onClick={() => handleFollowBack(user)}
                        >Follow Back</button>
                      </div>
                    )}
                  </>
                )}
                {/* Requested */}
                {requested.length > 0 && (
                  <>
                    <div className="insta-dropdown-subtitle">Requested</div>
                    {requested.map(user =>
                      <div key={user._id} className="insta-dropdown-card">
                        <span>{user.username}</span>
                        <span className="insta-requested-label">Requested</span>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </TopBar>

      {/* Search Results Section */}
      {searchResults.length > 0 && (
        <div className="insta-section">
          <div className="insta-section-header">
            <h2>Search Results</h2>
            <button
              className="dashboard-cross-btn"
              onClick={clearSearchResults}
              aria-label="Clear Search Results"
            >
              ×
            </button>
          </div>
          <div className="insta-card-list">
            {searchResults
            .filter(user =>
              user._id !== currentUser?._id &&
              user.email !== currentUser?.email // Exclude self
            )
            .length === 0 ? (
              <div className="insta-empty">No results found.</div>
            ) : searchResults
            .filter(user =>
              user._id !== currentUser?._id &&
              user.email !== currentUser?.email // Exclude self
            )
            .map(user => {
              // Determine status
              const isFollowing = friends.some(f => f._id === user._id);
              const isRequested = requested.some(r => r._id === user._id);
              const isRequest = requests.some(rq => rq._id === user._id);
              
              let button;
              if (isFollowing) {
                button = (
                  <button
                    className="insta-btn insta-btn-danger"
                    onClick={async () => {
                      const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/users/unfollow/${user._id}`, {
                        method: "POST"
                      });
                      if (res && res.ok) {
                        setFriends(friends.filter(u => u._id !== user._id));
                        setMsg("Unfollowed!");
                        setSnackbarOpen(true);
                      }
                    }}
                  >Unfollow</button>
                );
              } else if (isRequested) {
                button = (
                  <button className="insta-btn insta-btn-disabled" disabled>
                    Requested
                  </button>
                );
              } else if (isRequest) {
                button = (
                  <button className="insta-btn insta-btn-disabled" disabled>
                    Pending
                  </button>
                );
              } else {
                button = (
                  <button
                    className="insta-btn insta-btn-primary"
                    onClick={async () => {
                      const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/users/follow/${user._id}`, {
                        method: "POST"
                      });
                      if (res && res.ok) {
                        setMsg("Follow request sent!");
                        setSnackbarOpen(true);
                        setRequested([...requested, user]);
                      }
                    }}
                  >Follow</button>
                );
              }

              return (
                <div key={user._id} className="insta-card insta-search-card">
                  <div className="insta-avatar">
                    {user.username ? user.username[0].toUpperCase() : "U"}
                  </div>
                  <div className="insta-card-content">
                    <div className="insta-username">{user.username}</div>
                    <div className="insta-email">{user.email}</div>
                  </div>
                  <div className="insta-card-actions">
                    {button}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Chat Sidebar */}
      {showSidebar && (
        <ChatSidebar
          friends={friends}
          lastMessages={lastMessages}
          onClose={() => setShowSidebar(false)}
          onChatSelect={userId => {
            setShowSidebar(false);
            navigate(`/chat/${userId}`);
          }}
        />
      )}

      {/* Welcome Message */}
      <div className="insta-welcome-section">
        <div className="insta-welcome-card">
          <span className="insta-welcome-wave">👋</span>
          <span className="insta-welcome-text">
            Welcome
            {currentUser && currentUser.username
              ? `, ${currentUser.username}`
              : ""}
            !
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="insta-main-content">
        <div className="insta-section">
          <h2>Suggested Users</h2>
          <div className="insta-scroll-list">
            {loading ? (
              <div className="insta-loader" />
            ) : (
              getAllUniqueUsers()
                .filter(user =>
                  user._id !== currentUser?._id &&
                  currentUser?.email !== user.email
                )
                .map(user => {
                  // Determine status
                  const isFollowing = friends.some(f => f._id === user._id);
                  const isRequested = requested.some(r => r._id === user._id);
                  const isRequest = requests.some(rq => rq._id === user._id);

                  let button;
                  if (isFollowing) {
                    button = (
                      <button
                        className="insta-btn insta-btn-danger insta-suggested-btn"
                        onClick={async () => {
                          const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/users/unfollow/${user._id}`, {
                            method: "POST"
                          });
                          if (res && res.ok) {
                            setFriends(friends.filter(u => u._id !== user._id));
                            setMsg("Unfollowed!");
                            setSnackbarOpen(true);
                          }
                        }}
                      >Unfollow</button>
                    );
                  } else if (isRequested) {
                    button = (
                      <button className="insta-btn insta-btn-disabled insta-suggested-btn" disabled>
                        Requested
                      </button>
                    );
                  } else if (isRequest) {
                    button = (
                      <button className="insta-btn insta-btn-disabled insta-suggested-btn" disabled>
                        Pending
                      </button>
                    );
                  } else {
                    button = (
                      <button
                        className="insta-btn insta-btn-primary insta-suggested-btn"
                        onClick={async () => {
                          const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/users/follow/${user._id}`, {
                            method: "POST"
                          });
                          if (res && res.ok) {
                            setMsg("Follow request sent!");
                            setSnackbarOpen(true);
                            setRequested([...requested, user]);
                          }
                        }}
                      >Follow</button>
                    );
                  }

                  return (
                    <div key={user._id} className="insta-scroll-card insta-suggested-card">
                      <div className="insta-suggested-avatar">
                        {user.username ? user.username[0].toUpperCase() : "U"}
                      </div>
                      <div className="insta-suggested-content">
                        <div className="insta-suggested-username">{user.username}</div>
                        <div className="insta-suggested-email">{user.email}</div>
                      </div>
                      {button}
                    </div>
                  );
                })
            )}
          </div>
        </div>
        
        <div className="insta-section">
          <h2>Friends / Chats</h2>
          <div className="insta-card-list">
            {friends.length === 0 ? (
              <div className="insta-empty">No friends yet.</div>
            ) : (
              friends.map(user =>
                userCard(
                  user,
                  <>
                    <button
                      className="insta-btn insta-btn-outline"
                      onClick={() => navigate(`/chat/${user._id}`)}
                    >
                      Message
                    </button>
                    <button
                      className="insta-btn insta-btn-danger"
                      onClick={async () => {
                        const res = await apiFetch(`https://razchat-backend-1.onrender.com/api/users/unfollow/${user._id}`, {
                          method: "POST"
                        });
                        if (res && res.ok) {
                          setFriends(friends.filter(u => u._id !== user._id));
                          setMsg("Unfollowed!");
                          setSnackbarOpen(true);
                        }
                      }}
                    >
                      Unfollow
                    </button>
                  </>,
                  true
                )
              )
            )}
          </div>
        </div>
        {snackbarOpen && (
          <div className="insta-snackbar">{msg}</div>
        )}
      </div>
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />
    </div>
  );
}

// Move subscribeUserToPush and urlBase64ToUint8Array outside the Dashboard component
async function subscribeUserToPush(token) {
  if ('serviceWorker' in navigator && 'PushManager' in window) {
    const reg = await navigator.serviceWorker.ready;
    const publicKey = "BPrBIEvEEgk92Bxb9xl4AajOAfPY9kvp_v06w11TxFyRQoXfsZowvb6GQ1MrUZqjhsCranLNZBXvq-4xDPT6bBY";
    const convertedVapidKey = urlBase64ToUint8Array(publicKey);
    try {
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });
      await apiFetch("https://razchat-backend-1.onrender.com/api/users/subscribe", {
        method: "POST",
        body: JSON.stringify({ subscription })
      });
    } catch (err) {
      // User may have denied permission or already subscribed
    }
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}